import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    PredefinedAlertDto,
    PredefinedAlertsDto,
} from "@station/data/dto/predefined-alert.dto";
import {
    PredefinedAlertDtoSchema,
    PredefinedAlertsDtoSchema,
} from "@station/data/dto/predefined-alert.dto";
import {
    PredefinedAlert,
    PredefinedAlerts,
} from "@station/domain/models/predefined-alert.model";

import { injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class PredefinedAlertMapper {
    map(
        predefinedAlertDto: PredefinedAlertDto,
    ): Result<PredefinedAlert, ModelError<typeof PredefinedAlert>> {
        const predefinedAlertDtoParsed =
            PredefinedAlertDtoSchema.safeParse(predefinedAlertDto);

        if (!predefinedAlertDtoParsed.success) {
            console.error(predefinedAlertDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return PredefinedAlert.createWithRecovery({
            id: predefinedAlertDto.id,
            name: predefinedAlertDto.name,
        });
    }

    mapList(predefinedAlertsDto: PredefinedAlertsDto): PredefinedAlerts {
        const predefinedAlertsDtoParsed =
            PredefinedAlertsDtoSchema.safeParse(predefinedAlertsDto);

        if (!predefinedAlertsDtoParsed.success) {
            console.error(predefinedAlertsDtoParsed.error.issues);
            return PredefinedAlerts.empty();
        }

        const predefinedAlerts = predefinedAlertsDto.mapNotNull(
            (predefinedAlertDto) =>
                this.map(predefinedAlertDto).match(
                    (sensorAlert) => sensorAlert,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping sensor alerts:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
        );

        return PredefinedAlerts.create({
            predefinedAlerts,
            count: predefinedAlerts.length,
        }).unwrapOr(PredefinedAlerts.empty());
    }
}
